<template>
  <v-container class="pa-0">
    <v-fab-transition>
      <v-btn
        style="width: 40px; height: 40px; top: 24px; left: 24px"
        v-show="buttonVisible && !drawerVisible"
        dark
        absolute
        top
        left
        fab
        @click="toggleDrawer"
      >
        <v-icon>$iconChevronRight</v-icon>
      </v-btn>
    </v-fab-transition>

    <v-navigation-drawer
      v-model="drawerVisible"
      :permanent="drawerVisible"
      class="navigation-drawer"
      absolute
      dark
      disableOverlay
      color="#1A1A1A"
      width="20%"
    >
      <v-list class="ma-6 pa-0">
        <v-container class="ma-0 pa-0">
          <v-row no-gutters align="center" justify="space-between">
            <router-link to="/">
              <niko-icon></niko-icon>
            </router-link>
            <v-btn class="float-right" icon @click="toggleDrawer">
              <v-icon>$iconChevronLeft</v-icon>
            </v-btn>
          </v-row>

          <v-row dense class="mt-6">
            <v-col class="flex-grow-1">
              <h2 class="font-weight-bold">
                {{ project.name || $t("project.untitled") }}
              </h2>
            </v-col>
            <v-col
              v-if="state === 'in_configuration' || state === 'in_testing'"
              align-self="center"
              class="flex-grow-0"
            >
              <v-btn icon class="float-right">
                <v-icon
                  v-if="state === 'in_configuration'"
                  @click="editingModal()"
                  >$iconPencil</v-icon
                >
                <v-icon
                  v-if="state === 'in_testing'"
                  @click="openEquipmentModal()"
                  >$iconWrench</v-icon
                >
              </v-btn>
            </v-col>
          </v-row>

          <v-row no-gutters dense>
            <v-col>
              <span class="caption">{{ projectState() }}</span>
            </v-col>
          </v-row>

          <v-row class="mb-2">
            <v-col class="d-flex justify-center">
              <niko-button
                :disabled="project.state === 'completed'"
                v-if="state"
                :text="$t(`project.change_state.button.${state}`)"
                @onClick="openStateChangeModal()"
              />
            </v-col>
          </v-row>

          <v-divider color="#4C4C4C" class="ml-2"></v-divider>

          <v-list-group
            nav
            :value="building.building_id === building_id"
            :append-icon="null"
            :prepend-icon="
              building.building_id === building_id
                ? '$iconChevronDown'
                : '$iconChevronRight'
            "
            v-for="(building, index) in project.buildings"
            :key="index"
            class="sub-group-no-padding mb-2 mt-2"
            @click="selectBuilding(building)"
          >
            <template slot="activator">
              <v-list class="py-0">
                <v-list-item class="ml-2">
                  <v-list-item-content class="py-2">
                    <v-list-item-title>
                      <h3>
                        {{ building.name }}
                      </h3>
                    </v-list-item-title>
                  </v-list-item-content>
                  <niko-container-dropdown
                    v-if="possibleToEditBuilding(building.building_id)"
                    @clicked="openModal($event)"
                    name="building"
                    :items="[
                      $t('building.edit'),
                      ...(project.buildings.length > 1
                        ? $t('building.delete')
                        : []),
                    ]"
                    width="112px"
                    height="96px"
                    class="ml-2 mt-1"
                  />
                </v-list-item>
              </v-list>
            </template>

            <v-list
              class="pt-0 pb-0 pl-2 ml-3"
              style="margin-right: -24px !important"
            >
              <v-list-item-group mandatory>
                <v-list-item
                  style="border-left: 1px solid white"
                  :class="{
                    'v-list-item--active-overwrite':
                      floor.floor_id === floor_id,
                  }"
                  v-for="(floor, i) in building.floors"
                  :key="i"
                  @click="selectFloor(building, floor)"
                >
                  <v-list-item-content
                    style="max-width: fit-content"
                    class="ml-4 py-2"
                  >
                    <p>- {{ floor.name }}</p>
                  </v-list-item-content>
                  <niko-container-dropdown
                    v-if="possibleToEditFloor(floor.floor_id)"
                    @clicked="openModal($event)"
                    name="floor"
                    :items="[
                      $t('floor.edit'),
                      ...(building.floors.length > 1 ? $t('floor.delete') : []),
                    ]"
                    width="165px"
                    height="128px"
                    class="ml-2 mt-1"
                  />
                </v-list-item>

                <niko-text-button
                  v-if="state === 'in_configuration'"
                  color="primary"
                  icon-before="$iconAdd"
                  :text="$t('floor.add')"
                  alignLeft="true"
                  @onClick="startAddingFloor(building)"
                  :class="
                    project.buildings.length === 1
                      ? 'ml-2 mt-2 mb-0'
                      : 'ml-2 mt-2 mb-4'
                  "
                ></niko-text-button>
              </v-list-item-group>
            </v-list>
          </v-list-group>
        </v-container>

        <niko-text-button
          v-if="state === 'in_configuration'"
          color="primary"
          icon-before="$iconAdd"
          :text="$t('building.add')"
          alignLeft="true"
          @onClick="startAddingBuilding"
          class="mt-2"
        ></niko-text-button>
      </v-list>
    </v-navigation-drawer>
    <niko-modal
      v-if="modalVisible"
      :isVisible="modalVisible"
      :inputs="modalInputs"
      :title="modalTitle"
      :primaryButtonText="primaryButton()"
      :secondaryButtonText="$t('project.modal.button.cancel')"
      :caption="modalInfo"
      @onSubmit="updateData"
      @onClose="closeModal"
      @standardChanged="(value) => (editedStandardValue = value)"
    />
    <niko-modal-wrapper
      :title="confirmationModalTitle"
      :primaryButtonText="confirmationModalPrimaryButtonText"
      :secondaryButtonText="$t('project.modal.button.cancel')"
      :showModal="stateChangeModalOpen"
      @onClose="closeStateChangeModal()"
      @onSubmit="finishState()"
    >
      <div
        :class="showMissingEquipmentModalError ? 'pt-4' : 'py-4'"
        class="confirmation-modal-content body1"
        v-html="confirmationModalText"
      />
      <div
        v-if="showMissingEquipmentModalError"
        class="confirmation-modal-content body1"
      >
        <p class="text-red">
          {{
            missingEquipmentUsers.length > 0
              ? $t(
                  "project.change_state.modal.in_testing.error.shared_project_missing_equipment",
                  {
                    users: missingEquipmentUsers
                      .map((user) => user.name)
                      .join(", "),
                  }
                )
              : $t(
                  "project.change_state.modal.in_testing.error.missing_equipment"
                )
          }}
          <v-icon>$iconWrench</v-icon>
        </p>
      </div>
    </niko-modal-wrapper>
    <v-snackbar
      :timeout="0"
      v-model="showMissingEquipmentSnackBar"
      color="error"
      top
      centered
    >
      {{ $t("project.change_state.snackbar.missing_equipment") }}

      <v-btn icon>
        <v-icon
          class="snackbar-icon"
          @click="showMissingEquipmentSnackBar = false"
          >$iconClose
        </v-icon>
      </v-btn>
    </v-snackbar>
    <testing-equipment-modal
      :mode="equipmentModalMode"
      :isVisible="isEquipmentModalVisible"
      @onClose="isEquipmentModalVisible = false"
      @onSubmit="isEquipmentModalVisible = false"
    />
  </v-container>
</template>
<script>
import NikoIcon from "../icons/NikoIcon";
import { pickBy } from "lodash";
import NikoTextButton from "../UI/NikoTextButton";
import NikoModal from "../UI/NikoModal";
import { mapActions, mapMutations, mapState } from "vuex";
import NikoContainerDropdown from "../UI/NikoContainerDropdown";
import NikoButton from "../UI/NikoButton";
import NikoModalWrapper from "../UI/NikoModalWrapper";
import {
  getNextProjectState,
  getProjectStateChangeModalPrimaryButtonText,
  getProjectStateChangeModalTitle,
  getProjectStateChangeModalText,
  getProjectStatePresentationName,
} from "@/utils/projectStateHelpers";
import i18n from "../../i18n";
import TestingEquipmentModal from "./TestingEquipmentModal";

export default {
  name: "left-sidebar",
  components: {
    TestingEquipmentModal,
    NikoModalWrapper,
    NikoButton,
    NikoTextButton,
    NikoIcon,
    NikoModal,
    NikoContainerDropdown,
  },
  data: () => ({
    stateChangeModalOpen: false,
    drawerVisible: true,
    addingBuilding: false,
    addingFloor: false,
    isEditingProject: false,
    isEditingBuilding: false,
    isDeletingBuilding: false,
    isEditingFloor: false,
    isDeletingFloor: false,
    isRemovingPdf: false,
    modalVisible: false,
    editedStandardValue: null,
    isEquipmentModalVisible: false,
    equipmentModalMode: "edit",
    showMissingEquipmentSnackBar: false,
    showMissingEquipmentModalError: false,
  }),
  props: { buttonVisible: Boolean, project: Object },
  computed: {
    ...mapState("authStore", ["activeSubscription"]),
    ...mapState("projectStore", [
      "equipments",
      "project_id",
      "state",
      "users",
      "standard",
      "available_standards",
      "buildings",
      "missingEquipmentUsers",
    ]),
    ...mapState("buildingStore", {
      building_id: (state) => state.building_id,
      buildingName: (state) => state.name,
      buildingAddress: (state) => state.address,
      buildingComment: (state) => state.comments,
    }),
    ...mapState("floorStore", {
      floor_id: (state) => state.floor_id,
      floorName: (state) => state.name,
    }),
    ...mapState("roomStore", ["room_types"]),
    confirmationModalTitle() {
      return getProjectStateChangeModalTitle(this.state);
    },
    confirmationModalText() {
      return getProjectStateChangeModalText(this.state);
    },
    confirmationModalPrimaryButtonText() {
      return getProjectStateChangeModalPrimaryButtonText(this.state);
    },
    // TODO : if-else-s should be refactored, maybe move the modal to a separate component altogether? Perhaps one modal is doing too much?
    modalTitle() {
      if (this.addingBuilding) {
        return this.$i18n.t("project.modal.title.building");
      } else if (this.addingFloor) {
        return this.$i18n.t("project.modal.title.floor");
      } else if (this.isEditingProject) {
        return this.$i18n.t("project.modal.title.edit_project");
      } else if (this.isEditingBuilding) {
        return this.$i18n.t("project.modal.title.edit_building");
      } else if (this.isDeletingBuilding) {
        return this.$i18n.t("project.modal.title.delete_building", {
          building: this.buildingName,
        });
      } else if (this.isEditingFloor) {
        return this.$i18n.t("project.modal.title.edit_floor");
      } else if (this.isRemovingPdf) {
        return this.$i18n.t("project.modal.title.remove_plan", {
          floor: this.floorName,
        });
      } else {
        return this.$i18n.t("project.modal.title.delete_floor", {
          floor: this.floorName,
        });
      }
    },
    modalInfo() {
      return this.isEditingProject
        ? this.$t("project.modal.standard_info")
        : null;
    },
    modalInputs() {
      // TODO : Refactor if-elses
      if (this.addingFloor) {
        return [
          {
            label: this.$i18n.t("project.modal.input.floor"),
            value: this.floorName,
            mutation: "floorStore/updateFloorName",
          },
        ];
      } else if (this.isEditingProject) {
        return [
          {
            label: this.$i18n.t("project.modal.input.project"),
            value: this.project.name,
            mutation: "projectStore/editProjectName",
          },
          {
            label: this.$i18n.t("project_list.modal.add.fa_standard"),
            value: this.standard,
            selectValues: this.available_standards,
            selectItemText: "standard_name",
            selectItemValue: "standard_id",
            emitOnSelect: "standardChanged",
          },
        ];
      } else if (this.isEditingBuilding || this.addingBuilding) {
        return [
          {
            label: this.$i18n.t("project.modal.input.building"),
            value: this.buildingName,
            mutation: "buildingStore/updateBuildingName",
          },
          {
            label: this.$i18n.t("project.modal.input.address"),
            value: this.buildingAddress,
            mutation: "buildingStore/updateBuildingAddress",
          },
          {
            label: this.$i18n.t("project.modal.input.comment"),
            value: this.buildingComment,
            mutation: "buildingStore/updateBuildingComment",
          },
        ];
      } else if (this.isEditingFloor) {
        return [
          {
            label: this.$i18n.t("project.modal.input.floor"),
            value: this.floorName,
            mutation: "floorStore/updateFloorName",
          },
        ];
      } else {
        return [];
      }
    },
  },
  methods: {
    ...mapMutations("floorStore", ["setFloorData", "updateFloorName"]),
    ...mapMutations("buildingStore", [
      "setBuildingData",
      "resetBuildingState",
      "updateBuildingName",
    ]),
    ...mapMutations("projectStore", ["editProjectName"]),
    ...mapMutations("roomStore", ["resetRoomState"]),
    ...mapMutations("loaderStore", ["setIsLoading"]),
    ...mapActions("projectStore", [
      "addBuilding",
      "addFloor",
      "updateFloor",
      "updateProjectNameAndStandard",
      "updateBuilding",
      "deleteBuilding",
      "deleteFloor",
      "setProjectState",
      "resetRoomTypes",
      "getMissingEquipmentUsers",
    ]),
    ...mapActions("roomStore", ["getRoomTypes"]),
    projectState() {
      return getProjectStatePresentationName(this.state);
    },
    checkProjectTestingEquipment() {
      let isTestingEquipmentSet = true;
      this.buildings.forEach((building) => {
        building.floors.forEach((floor) => {
          floor.rooms.forEach((room) => {
            room.functional_tests.forEach((functional_test) => {
              if (
                functional_test.functional_test_results.length > 0 &&
                this.equipments.length === 0
              ) {
                isTestingEquipmentSet = false;
              }
            });
            room.illumination_tests.forEach((illumination_test) => {
              if (
                illumination_test.illumination_test_results.length > 0 &&
                this.equipments.length === 0
              ) {
                isTestingEquipmentSet = false;
              }
            });
          });
        });
      });
      return isTestingEquipmentSet;
    },
    async getIsTestingEquipmentSet() {
      let isTestingEquipmentSet = false;
      if (this.users.length > 0) {
        const requestSucceeded = await this.getMissingEquipmentUsers(
          this.project_id
        );
        if (requestSucceeded && this.missingEquipmentUsers.length === 0) {
          isTestingEquipmentSet = true;
        }
      } else {
        isTestingEquipmentSet = this.checkProjectTestingEquipment();
      }
      return isTestingEquipmentSet;
    },
    async finishState() {
      if (this.state === "in_testing") {
        this.setIsLoading(true);
        const isTestingEquipmentSet = await this.getIsTestingEquipmentSet();
        this.setIsLoading(false);
        if (!isTestingEquipmentSet) {
          this.showMissingEquipmentModalError = true;
          this.showMissingEquipmentSnackBar = true;
          return;
        }
      }
      await this.setProjectState({
        project_id: this.project_id,
        newState: getNextProjectState(this.state),
      });
      this.$router.push("/");
    },
    openStateChangeModal() {
      this.stateChangeModalOpen = true;
    },
    closeStateChangeModal() {
      this.stateChangeModalOpen = false;
      this.showMissingEquipmentModalError = false;
      this.showMissingEquipmentSnackBar = false;
    },
    toggleDrawer() {
      this.drawerVisible = !this.drawerVisible;
    },
    selectBuilding(building) {
      this.setBuildingData(building);
      this.setFloorData(building.floors[0]);
    },
    startAddingBuilding() {
      this.resetBuildingState();
      this.addingBuilding = true;
      this.modalVisible = true;
    },
    startAddingFloor(building) {
      this.updateFloorName(
        `${i18n.t("floor.floor")} ${building.floors.length}`
      );
      this.setBuildingData(building);
      this.addingFloor = true;
      this.modalVisible = true;
    },
    selectFloor(building, floor) {
      this.setBuildingData(building);
      this.setFloorData(floor);
    },
    closeModal() {
      this.allToZero();
    },
    editingModal() {
      // THIS CAN BE UNCOMMENTED FOR TESTING PURPOSES, TO CHANGE THE STATE BACK FROM COMPLETED
      // this.setProjectState("in_configuration");

      if (this.state !== "in_testing") {
        this.isEditingProject = true;
      }
      this.modalVisible = true;
    },
    openEquipmentModal() {
      this.isEquipmentModalVisible = true;
      this.equipmentModalMode = "edit";
    },
    possibleToEditBuilding(id) {
      return this.building_id === id && this.state === "in_configuration";
    },
    possibleToEditFloor(id) {
      return this.floor_id === id && this.state === "in_configuration";
    },
    showModal() {
      return (
        this.addingBuilding ||
        this.addingFloor ||
        this.isEditingProject ||
        this.isEditingBuilding ||
        this.isDeletingBuilding ||
        this.isEditingFloor ||
        this.isDeletingFloor ||
        this.isRemovingPdf
      );
    },
    primaryButton() {
      // TODO : refactor if-elses
      let buttonText = "";
      if (this.addingBuilding || this.addingFloor) {
        buttonText = this.$i18n.t("project.modal.button.add");
      } else if (this.isDeletingBuilding || this.isDeletingFloor) {
        buttonText = this.$i18n.t("project.modal.button.delete");
      } else if (this.isRemovingPdf) {
        buttonText = this.$i18n.t("project.modal.button.remove");
      } else {
        buttonText = this.$i18n.t("project.modal.button.done");
      }
      return buttonText;
    },
    openModal(data) {
      // TODO : refactor if-elses
      if (data.name === "building") {
        if (data.value === this.$i18n.t("building.edit")) {
          this.isEditingBuilding = true;
        } else {
          this.isDeletingBuilding = true;
        }
      } else if (data.name === "floor") {
        if (data.value === this.$i18n.t("floor.edit")) {
          this.isEditingFloor = true;
        } else if (data.value === this.$i18n.t("building.delete")) {
          this.isDeletingFloor = true;
        } else {
          this.isRemovingPdf = true;
        }
      }
      this.modalVisible = true;
    },
    async updateData() {
      // TODO : refactor if-elses
      if (this.addingBuilding) {
        await this.addBuilding();
      } else if (this.addingFloor) {
        this.addFloor();
      } else if (this.isEditingProject) {
        this.updateProjectNameAndStandard(this.editedStandardValue);
        this.resetRoomTypesAndGetNewList();
      } else if (this.isEditingBuilding) {
        this.updateBuilding(this.building_id);
      } else if (this.isDeletingBuilding) {
        await this.deleteBuildingData();
      } else if (this.isEditingFloor) {
        await this.updateFloor({ building_id: this.building_id });
      } else if (this.isDeletingFloor) {
        await this.deleteFloorData();
      } else {
        console.log("Remove pdf");
      }
      this.allToZero();
    },
    resetRoomTypesAndGetNewList() {
      if (
        !this.editedStandardValue ||
        this.editedStandardValue === this.standard
      ) {
        return;
      }
      this.getRoomTypes(this.editedStandardValue);
      this.resetRoomState();
      const roomObjects = [];
      this.buildings.forEach((building) => {
        building.floors.forEach((floor) => {
          floor.rooms.forEach((room) => {
            if (room.room_type_id !== -1) {
              const roomData = {
                room_id: room.room_id,
                name: room.name,
                room_image_url: room.room_image_url,
                room_type: "",
                room_type_id: -1,
                room_info_comment: room.room_info_comment.trim(),
                measurement_points_comment:
                  room.measurement_points_comment.trim(),
                longer_size_length: room.longer_size_length,
                distance_between_points_x: room.distance_between_points_x,
                distance_between_points_y: room.distance_between_points_y,
                measurement_height: room.measurement_height,
              };
              roomObjects.push({
                room: pickBy(
                  roomData,
                  (prop) => prop !== undefined && prop !== ""
                ),
                building_id: building.building_id,
                floor_id: floor.floor_id,
              });
            }
          });
        });
      });
      if (roomObjects.length > 0) {
        this.resetRoomTypes(roomObjects);
      }
    },
    allToZero() {
      this.modalVisible = false;
      this.addingBuilding = false;
      this.addingFloor = false;
      this.isEditingProject = false;
      this.isEditingBuilding = false;
      this.isDeletingBuilding = false;
      this.isEditingFloor = false;
      this.isRemovingPdf = false;
      this.isDeletingFloor = false;
    },
    async deleteFloorData() {
      const selectedBuilding = this.project.buildings.find(
        (building) => building.building_id === this.building_id
      );
      const deletedFloorIndex = selectedBuilding.floors.findIndex(
        (floor) => floor.floor_id === this.floor_id
      );
      await this.deleteFloor(this.floor_id);
      this.selectFloor(
        selectedBuilding,
        selectedBuilding.floors[deletedFloorIndex ? deletedFloorIndex - 1 : 0]
      );
    },
    async deleteBuildingData() {
      const selectedBuildingIndex = this.project.buildings.findIndex(
        (building) => building.building_id === this.building_id
      );
      await this.deleteBuilding(this.building_id);
      this.selectBuilding(
        this.project.buildings[
          selectedBuildingIndex ? selectedBuildingIndex - 1 : 0
        ]
      );
    },
  },
  watch: {
    project(value) {
      const isProjectWithoutEquipment =
        value.state === "in_testing" && !this.equipments?.length;
      this.isEquipmentModalVisible = isProjectWithoutEquipment;
      this.equipmentModalMode = isProjectWithoutEquipment ? "add" : "edit";
    },
  },
};
</script>

<style lang="scss">
.confirmation-modal-content {
  p,
  li {
    font-size: 16px !important;
    line-height: 24px !important;
    font-weight: 400 !important;
  }
  p,
  ul,
  ol {
    margin-bottom: 24px !important;
  }
}

.navigation-drawer {
  min-width: 234px;

  .v-list-item--link,
  .v-list-item,
  .v-list-group__header,
  .v-list-item__content {
    min-height: 40px !important;
    color: white !important;
  }

  .v-application .mb-0 {
    margin-bottom: 8px !important;
  }

  .theme--dark.v-list-item--active::before {
    opacity: 0 !important;
  }

  @media (hover: hover) and (pointer: fine) {
    .theme--dark.v-list-item--active:hover::before {
      opacity: 0 !important;
    }
  }

  .theme--dark.v-list-item--active-overwrite::before {
    opacity: 0.24 !important;
  }

  .v-list-item__icon {
    margin: 8px !important;
  }

  .v-list-group > .row {
    height: 40px !important;
  }

  .v-list-item__action {
    margin: 0 !important;
  }
}

.v-navigation-drawer__content::-webkit-scrollbar {
  margin-right: 8px;
  color: #aba499;
  width: 8px;
  height: 80%;
  border-radius: 100px !important;
}

.v-navigation-drawer__content::-webkit-scrollbar-thumb {
  background-color: #454a4d;
  border-radius: 100px !important;
}

.v-navigation-drawer__content::-webkit-scrollbar-corner {
  background: rgba(0, 0, 0, 0);
}

.v-navigation-drawer__content::-webkit-scrollbar-thumb:hover {
  background-color: #575e62;
}

.v-navigation-drawer__content::-webkit-scrollbar-thumb:active {
  background-color: #484e51;

  .v-list-item__icon,
  .v-list-group__header__append-icon {
    display: none;
  }
}

.sub-group-no-padding {
  div {
    padding-left: 0 !important;
  }
}

.v-text-field__details {
  padding-left: 0 !important;
}

.v-input.error--text:before {
  content: "";
  height: calc(100% + 20px);
  width: 4px;
  background: var(--v-error-base);
  position: absolute;
  left: -12px;
  top: -24px;
}
</style>
